import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { num } from "../lib/format";
import { Icon } from "../lib/icons";
import { loadAllSaved, REPORT_KEYS } from "../lib/viewState";
import { cn } from "./ui";

// Ctrl+K / "/" palette — also what the header search box opens. One list: the old "search in the
// report" action first (Enter keeps the previous header-search behaviour), then live product
// matches (server-side ILIKE via /products?q=), pages, and saved views.

const OPEN_EVENT = "wa:palette";

export function openPalette(): void {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

const PAGES: Array<{ to: string; label: string; icon: string }> = [
  { to: "/", label: "Обзор", icon: "overview" },
  { to: "/report/current", label: "Текущий остаток", icon: "box" },
  { to: "/report/daily", label: "По дням", icon: "calendarDay" },
  { to: "/report/weekly", label: "По неделям", icon: "calendar" },
  { to: "/report/monthly", label: "По месяцам", icon: "calendarRange" },
  { to: "/report/suppliers", label: "По поставщикам", icon: "truck" },
  { to: "/report/brands", label: "По брендам", icon: "tag" },
  { to: "/all-reports", label: "Все отчёты", icon: "reports" },
  { to: "/reconciliation", label: "Расхождения", icon: "scale" },
  { to: "/alerts", label: "Проблемы", icon: "alert" },
  { to: "/settings", label: "Настройки", icon: "settings" },
];
const REPORT_LABEL: Record<string, string> = {
  current: "Текущий остаток",
  daily: "По дням",
  weekly: "По неделям",
  monthly: "По месяцам",
  suppliers: "По поставщикам",
  brands: "По брендам",
};

type Item = { id: string; group: string; label: string; hint?: string; icon: string; run: () => void };
type ProductHit = { product_id: string; product_name: string; brand?: string | null; category?: string | null; closing_stock?: number | null };

export function CommandPalette() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const [products, setProducts] = useState<ProductHit[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [views, setViews] = useState<Array<{ id: string; name: string; rk: string }>>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // open: Ctrl/Cmd+K anywhere, "/" when not typing, or openPalette()
  useEffect(() => {
    const show = () => {
      setQ("");
      setActive(0);
      setOpen(true);
    };
    const onKey = (e: KeyboardEvent) => {
      const el = document.activeElement;
      const typing = el instanceof HTMLElement && (["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName) || el.isContentEditable);
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        show();
      } else if (e.key === "/" && !typing) {
        e.preventDefault();
        show();
      }
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener(OPEN_EVENT, show);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener(OPEN_EVENT, show);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => inputRef.current?.focus());
    loadAllSaved().then((all) => {
      const flat: Array<{ id: string; name: string; rk: string }> = [];
      for (const k of REPORT_KEYS) for (const v of all[k]) flat.push({ id: v.id, name: v.name, rk: k });
      setViews(flat);
    });
  }, [open]);

  // live product search, debounced; stale responses are dropped
  useEffect(() => {
    const t = q.trim();
    if (!open || t.length < 2) {
      setProducts([]);
      setLoadingProducts(false);
      return;
    }
    let alive = true;
    setLoadingProducts(true);
    const timer = window.setTimeout(() => {
      api
        .get<ProductHit[]>("/products", { q: t, page: 1, page_size: 8 })
        .then((r) => alive && setProducts(r.data))
        .catch(() => alive && setProducts([]))
        .finally(() => alive && setLoadingProducts(false));
    }, 250);
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [q, open]);

  const close = () => setOpen(false);
  const go = (to: string) => {
    close();
    navigate(to);
  };

  const items = useMemo<Item[]>(() => {
    const t = q.trim();
    const low = t.toLowerCase();
    const out: Item[] = [];
    if (t) {
      const base = location.pathname.startsWith("/report/") ? location.pathname : "/report/current";
      out.push({
        id: "search",
        group: "Поиск",
        label: `Искать «${t}» в отчёте`,
        hint: base === "/report/current" ? "Текущий остаток" : REPORT_LABEL[base.split("/")[2]],
        icon: "search",
        run: () => go(`${base}?q=${encodeURIComponent(t)}`),
      });
    }
    for (const p of products)
      out.push({
        id: `p:${p.product_id}`,
        group: "Товары",
        label: p.product_name,
        hint: [p.product_id, p.brand, p.category].filter(Boolean).join(" · ") + (p.closing_stock != null ? ` · остаток ${num(p.closing_stock)}` : ""),
        icon: "box",
        run: () => go(`/product/${encodeURIComponent(p.product_id)}`),
      });
    for (const pg of PAGES)
      if (!low || pg.label.toLowerCase().includes(low))
        out.push({ id: `pg:${pg.to}`, group: "Страницы", label: pg.label, icon: pg.icon, run: () => go(pg.to) });
    for (const v of views)
      if (!low || v.name.toLowerCase().includes(low))
        out.push({
          id: `v:${v.rk}:${v.id}`,
          group: "Сохранённые виды",
          label: v.name,
          hint: REPORT_LABEL[v.rk],
          icon: "star",
          run: () => go(`/report/${v.rk}?view=${v.id}`),
        });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, products, views, location.pathname]);

  useEffect(() => setActive(0), [q, products.length]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active]);

  if (!open) return null;

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(items.length - 1, a + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      items[active]?.run();
    } else if (e.key === "Escape") {
      close();
    }
  };

  let lastGroup = "";
  return (
    <div className="fixed inset-0 z-[95] bg-black/30 p-4 pt-[12vh] backdrop-blur-sm animate-fade-in print:hidden" onClick={close}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Поиск"
        className="mx-auto w-full max-w-xl overflow-hidden rounded-xl border border-border bg-surface shadow-pop animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2.5 border-b border-border px-4">
          <Icon name="search" size={16} className="flex-none text-text-2" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder="Товар, SKU, бренд, отчёт или вид…"
            className="h-12 min-w-0 flex-1 bg-transparent text-base placeholder:text-muted focus:outline-none"
          />
          {loadingProducts && <span className="h-4 w-4 flex-none animate-spin rounded-full border-2 border-accent border-t-transparent" />}
          <kbd className="flex-none rounded border border-border px-1.5 py-0.5 text-2xs text-text-2">Esc</kbd>
        </div>
        <div ref={listRef} className="scroll-thin max-h-[56vh] overflow-y-auto p-2">
          {items.length === 0 && (
            <div className="px-3 py-8 text-center text-sm text-text-2">
              {loadingProducts ? "Ищем…" : "Ничего не найдено"}
            </div>
          )}
          {items.map((it, i) => {
            const head = it.group !== lastGroup ? it.group : null;
            lastGroup = it.group;
            return (
              <div key={it.id}>
                {head && <div className="px-3 pb-1 pt-2.5 text-2xs font-semibold uppercase tracking-wider text-text-2/80">{head}</div>}
                <button
                  type="button"
                  data-idx={i}
                  onMouseMove={() => setActive(i)}
                  onClick={it.run}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left",
                    i === active ? "bg-accent-weak text-accent" : "text-text",
                  )}
                >
                  <Icon name={it.icon} size={16} className={cn("flex-none", i === active ? "text-accent" : "text-text-2")} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-base font-medium">{it.label}</span>
                    {it.hint && <span className="block truncate text-xs text-text-2">{it.hint}</span>}
                  </span>
                  {i === active && <Icon name="arrowRight" size={16} className="flex-none" />}
                </button>
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-4 border-t border-border bg-surface-3 px-4 py-2 text-xs text-text-2">
          <span>↑↓ — выбор</span>
          <span>Enter — открыть</span>
          <span className="ml-auto">Ctrl+K — открыть поиск</span>
        </div>
      </div>
    </div>
  );
}
